import {
  Autocomplete,
  InputAdornment,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { Fragment, SyntheticEvent, useEffect, useRef, useState } from "react";
import { UserSessionDetails } from "~/features/auth/auth.types";
import { ClientsDto } from "~/features/clients/clients.types";
import { ClientOption } from "~/features/expertises/community-corner/components/ClientTypeAheadDropdown";
import {
  getAutocompleteOptionClasses,
  getAutocompleteTheme,
} from "../utilities/autoCompleteTheme";
import { shouldShowBranchForPersona } from "../utilities/clientContextUtils";
import SearchIcon from "./svgs/SearchIcon";

export function mapClientDtoToOption({
  clientSk,
  clientName,
  clientEmail,
  clientPhoneNumber,
  displayStrategyName,
  clientCity,
  clientState,
  clientPostalCode,
  branchName,
  locationName,
  clientType,
}: ClientsDto): ClientOption {
  return {
    id: clientSk ?? 0,
    name: clientName || "",
    clientEmail: clientEmail?.toLowerCase() === "unknown" ? "" : clientEmail,
    clientPhoneNumber:
      clientPhoneNumber?.toLowerCase() === "unknown" ? "" : clientPhoneNumber,
    displayStrategyName: displayStrategyName || "",
    clientSk,
    clientCity: clientCity || "",
    clientState: clientState || "",
    clientPostalCode: clientPostalCode || "",
    branchName: branchName || "",
    locationName: locationName || "",
    clientType: clientType || "",
  };
}

export interface ClientOptionDetailSegment {
  text: string;
  isLocation: boolean;
}

/**
 * Builds the ordered segments of the search-result detail line. The Location
 * segment is flagged `isLocation` (retained for the data model + its tests);
 * as of US23748 UAT the renderer prints every segment as plain text, so the
 * flag no longer drives any styling. Branch is included only for
 * branch-visible personas. US 24656.
 */
export function buildClientOptionSegments(
  option: ClientOption,
  showBranch: boolean
): ClientOptionDetailSegment[] {
  const cityStatePostal = [
    option.clientCity,
    option.clientState && `, ${option.clientState}`,
    option.clientPostalCode && ` ${option.clientPostalCode}`,
  ]
    .filter(Boolean)
    .join("");

  const segments: ClientOptionDetailSegment[] = [];
  if (cityStatePostal) segments.push({ text: cityStatePostal, isLocation: false });
  if (showBranch && option.branchName?.trim())
    segments.push({ text: option.branchName.trim(), isLocation: false });
  if (option.locationName?.trim())
    segments.push({ text: option.locationName.trim(), isLocation: true });
  if (option.clientType?.trim())
    segments.push({ text: option.clientType.trim(), isLocation: false });
  return segments;
}

const ClientQuicklinkDropdown = ({
  id,
  label,
  onSelect,
  onBlurAction,
  helperText,
  error,
  isMobile = false,
  user,
  searchClients,
  autoFocus = true,
  compact = false,
}: {
  id: string;
  label: string;
  onSelect: (option: ClientOption | string) => void;
  onBlurAction: () => void;
  helperText?: string;
  error?: boolean;
  isMobile?: boolean;
  user?: UserSessionDetails | null;
  searchClients: (query: string) => Promise<ClientOption[]>;
  /** Focus the input on mount — the header opens this on a click, so it
   *  defaults on; a search box that is simply part of a page passes false. */
  autoFocus?: boolean;
  /**
   * In a page's flow rather than the header: no fixed header height (the
   * header's `h-[15px]` let the field spill over whatever sat below it) and
   * the pane's one control height, 34px, with 13px text (story 28633).
   */
  compact?: boolean;
}) => {
  const [value, setValue] = useState<ClientOption | null | string>(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeSearchRef = useRef(0);
  const clientSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) clientSearchRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const handleBlur = () => {
    const selectedOption = options.find((option) => option.name === inputValue);
    if (selectedOption) {
      setValue(selectedOption);
      onSelect(selectedOption);
    }
    onBlurAction();
  };

  const handleValueChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: ClientOption | null | string
  ) => {
    // Only update value if it's an actual selection from dropdown, not on Enter key
    if (newValue && typeof newValue === "object") {
      setValue(newValue);
      onSelect(newValue);
    } else if (newValue === null && event.type !== "keydown") {
      // Allow clearing only if it's not from keydown event
      setValue(newValue);
      onSelect("");
    }
  };

  const handleInputChange = (
    event: SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (newInputValue === "" || newInputValue.length < 3) {
      setOptions([]);
      setLoading(false);
      return;
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      setOptions([]);
      setLoading(true);
      const searchId = ++activeSearchRef.current;
      try {
        const results = await searchClients(newInputValue);
        if (searchId === activeSearchRef.current) {
          setOptions(results);
        }
      } catch {
        // ignore — keep options empty
      } finally {
        if (searchId === activeSearchRef.current) {
          setLoading(false);
        }
      }
    }, 300);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === "Tab") {
      event.preventDefault();
    }
  };

  // Move this function outside the component
  const renderClientOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: ClientOption,
    isMobile: boolean
  ) => {
    const segments = buildClientOptionSegments(
      option,
      shouldShowBranchForPersona(user?.userAccessLevel)
    );

    return (
      <li {...props} className={getAutocompleteOptionClasses(isMobile, false)}>
        <div
          className={`flex w-full gap-2 ${
            isMobile ? "flex-col" : "flex-row items-center"
          }`}
        >
          <span className="font-semibold text-gray-900 text-xs xl1440:text-sm mb-[2px]">
            {option.name}
          </span>
          <span className="text-2xs xl1440:text-xs text-gray-600">
            {segments.map((segment, index) => (
              <Fragment key={index}>
                {index > 0 && " - "}
                {segment.text}
              </Fragment>
            ))}
          </span>
        </div>
      </li>
    );
  };

  return (
    <div className={`${id}-container`}>
      <ThemeProvider theme={getAutocompleteTheme(isMobile)}>
        <Autocomplete
          id={id}
          value={value || undefined}
          size="small"
          onChange={handleValueChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onBlur={handleBlur}
          options={options}
          getOptionLabel={(option) =>
            option && typeof option === "object" ? option.name : ""
          }
          getOptionKey={(option) => {
            return option && typeof option === "object" ? option.id : 0;
          }}
          filterOptions={(x) => x} // Disable client-side filtering
          renderOption={(props, option) =>
            renderClientOption(props, option as ClientOption, isMobile)
          }
          clearOnEscape
          selectOnFocus={false}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={clientSearchRef}
              placeholder={label}
              variant="outlined"
              className={
                compact
                  ? "bg-white"
                  : "bg-white h-[15px] xl1440:h-[40px] text-2xs xl1440:text-sm"
              }
              sx={
                compact
                  ? {
                      "& .MuiInputBase-root": {
                        height: "34px",
                        paddingTop: 0,
                        paddingBottom: 0,
                        fontSize: "13px",
                      },
                    }
                  : undefined
              }
              error={error}
              helperText={helperText}
              onKeyDown={handleKeyDown}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="w-[16px] h-[16px] xl1440:w-[25px] xl1440:h-[24px]" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          className="w-full"
          loading={loading}
        />
      </ThemeProvider>
    </div>
  );
};

export default ClientQuicklinkDropdown;
